import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
// components
import { Box, Typography } from '@mui/material'
import AdxButton from 'components/custom/AdxButton'
import AdxInfoModal from 'components/reusable/modal/AdxInfoModal'
import SnackbarRecruitment from 'module/recruitment/reusable/SnackbarRecruitment'
import dropdownOptions from 'module/time-attendance/components/dropdownOptions'

// services
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { getSections } from 'module/time-attendance/components/sectionsConfig'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { getAllActiveDepartments } from 'services/api/modules/performance/appraisal/department'
import { useNavigate, useParams } from 'react-router-dom'
import {
  deleteShift,
  getActiveEmployees,
  getShiftDetails,
  patchActivateDeactivateShift,
  postCreateShift,
  searchLocations,
  putEditShift,
} from 'services/api/time-attendance'
import { calculateTimeDifferenceFormatted, formatToYYYYMMDD } from 'services/helper/DateTime'
import { addDays } from 'date-fns'
import useDebounce from 'services/hooks/useDebounce'

// icons
import CloseIcon from '@mui/icons-material/Close'
import CircularProgress from '@mui/material/CircularProgress'
import useLocationDetection from 'module/time-attendance/hooks/useLocationDetection'

const DEFAULT_LOCATION_FIELDS = {
  name: '',
  address: '',
  latitude: '',
  longitude: '',
}

const getApiErrorPayload = (error) => error?.response?.data || error?.data || error
const getApiErrorStatus = (error) => error?.response?.status || error?.status

const parseOutOfRadiusAction = (value) => {
  if (value === 'warn' || value === 2 || value === '2') return 'warn'
  return 'block'
}

const getLocationConfigFromShift = (shiftData) => {
  const locationConfig = shiftData?.location_config

  return {
    enabled: Boolean(locationConfig?.enabled ?? shiftData?.use_default_location),
    locationName:
      locationConfig?.location_name ??
      shiftData?.location_name ??
      shiftData?.zone_name ??
      '',
    locationAddress: locationConfig?.location_address ?? shiftData?.location_address ?? '',
    locationLatitude:
      locationConfig?.location_latitude ??
      shiftData?.location_latitude ??
      shiftData?.latitude ??
      '',
    locationLongitude:
      locationConfig?.location_longitude ??
      shiftData?.location_longitude ??
      shiftData?.longitude ??
      '',
    allowedRadiusMeters:
      locationConfig?.allowed_radius_meters ??
      shiftData?.allowed_radius_meters ??
      shiftData?.radius ??
      '',
    outOfRadiusAction: parseOutOfRadiusAction(
      locationConfig?.out_of_radius_action ?? shiftData?.out_of_radius_action
    ),
  }
}

const ShiftForm = () => {
  const sectionRefs = useRef([])

  const navigate = useNavigate()
  const params = useParams()
  const [editable, setEditable] = useState(true)
  const isEdit = Boolean(params.shiftId)
  const [restrictByLocation, setRestrictByLocation] = useState(false)
  const [locationSearch, setLocationSearch] = useState('')
  const [selectedLocation, setSelectedLocation] = useState(DEFAULT_LOCATION_FIELDS)
  const [applyDetectedLocationOnResolve, setApplyDetectedLocationOnResolve] = useState(false)
  const hasLocationSearchSupport = true

  const schema = Yup.object().shape({
    name: Yup.string()
      .min(2, 'Name must be at least 2 characters long')
      .max(50, 'Name cannot be longer than 50 characters'),
    description: Yup.string()
      .nullable()
      .optional()
      .max(300, 'Description cannot be longer than 300 characters'),
  })

  const { data: departments } = useQuery(
    ['getAllActiveDepartments'],
    async () => {
      const { data } = await getAllActiveDepartments()
      return data
    },
    {
      refetchOnWindowFocus: false,
    }
  )

  const { data: employees } = useQuery(
    ['getAllActiveEmployees'],
    async () => {
      const { data } = await getActiveEmployees()
      return data
    },
    {
      refetchOnWindowFocus: false,
    }
  )

  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarStatus, setSnackbarStatus] = useState('success') // 'success' | 'error'
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [disableImpact, setDisableImpact] = useState(null)
  const [disableImpactModalOpen, setDisableImpactModalOpen] = useState(false)

  const queryClient = useQueryClient()

  const { mutate: mutateCreateShift, isLoading: mutateCreateShiftIsLoading } = useMutation(
    async (payload) => await postCreateShift(payload),
    {
      onSuccess: (response) => {
        setSnackbarStatus('success')
        setSnackbarMessage('Changes saved.')
        setSnackbarOpen(true)
        queryClient.invalidateQueries(['getAllShifts'])
        queryClient.invalidateQueries(['getShiftDetails'])
        navigate('/app/management-centre/company-management/time-attendance', {
          state: { snackbarStatus: isDraft ? 4 : 1 },
        })
        console.log(response)
      },
      onError: (error) => {
        setSnackbarStatus('error')
        setSnackbarMessage(error.response?.data || 'This shift name is already in use. Please choose a different name.')
        setSnackbarOpen(true)
        console.error('Mutation Error:', error)
        console.error('Error Details:', error.response?.data)
      },
    }
  )

  const { mutate: mutateEditShift, isLoading: mutateEditShiftIsLoading } = useMutation(
    async (payload) => {
      if (editable) {
        return await putEditShift(params.shiftId, payload)
      }
      throw new Error('Shift is not editable')
    },
    {
      onSuccess: async (response) => {

        await queryClient.invalidateQueries(['getAllShifts'])
        await queryClient.invalidateQueries(['getShiftDetails', params.shiftId])
        setEditable(false)
        //setErrorSnackbarOpen(true)
        setSnackbarStatus('success')
        setSnackbarMessage(response?.message || 'Changes saved.')
        setSnackbarOpen(true)
        const updatedShiftId = response?.data?.shift?.id
        if (updatedShiftId && String(updatedShiftId) !== String(params.shiftId)) {
          navigate(`/app/management-centre/company-management/time-attendance/shift/${updatedShiftId}`)
        }
        console.log(response)
      },
      onError: (error) => {
        //setErrorSnackbarOpen(true)
        setSnackbarMessage('Make sure "Working Mode" is selected.') //Sorry for hardcoding the error message will check upon this later
        setSnackbarStatus('error')
        setSnackbarMessage(error.response?.data || 'An error has occured. Please try again.')
        setSnackbarOpen(true)
        console.error('Mutation Error:', error)
        console.error('Error Details:', error.response?.data)
      },
    }
  )

  const { mutate: activateOrDeactivateShift, isLoading: activateOrDeactivateShiftIsLoading } =
    useMutation(async (payload) => await patchActivateDeactivateShift(params.shiftId, payload), {
      onSuccess: async (response) => {
        await queryClient.invalidateQueries(['getAllShifts'])
        await queryClient.invalidateQueries(['getShiftDetails', params.shiftId])

        setDisableImpactModalOpen(false)
        setDisableImpact(null)
        setSnackbarStatus('success')
        setSnackbarMessage(response?.message || 'Shift status updated.')
        setSnackbarOpen(true)
        console.log(response)
      },
      onError: (error) => {
        const errorStatus = getApiErrorStatus(error)
        const errorPayload = getApiErrorPayload(error)

        if (errorStatus === 409 && errorPayload?.code === 'SHIFT_HAS_ACTIVE_CLOCKINS') {
          setDisableImpact(errorPayload)
          setDisableImpactModalOpen(true)
          return
        }

        setSnackbarStatus('error')
        setSnackbarMessage(errorPayload?.message || errorPayload || 'An error has occured. Please try again.')
        setSnackbarOpen(true)
        console.log(error)
      },
    })

  const handleDisableShift = () => {
    activateOrDeactivateShift({ status: 'inactive', effective_from: 'today' })
  }

  const handleDisableFromTomorrow = () => {
    activateOrDeactivateShift({ status: 'inactive', effective_from: 'tomorrow' })
  }

  const { mutate: mutateDeleteShift, isLoading: mutateDeleteShiftIsLoading } = useMutation(
    async (payload) => {
      if (shiftData?.status === 'inactive' || shiftData?.status === 'draft') {
        return await deleteShift(payload)
      }
    },
    {
      onSuccess: async (response) => {
        console.log(response)
        setSnackbarStatus('success')
        setSnackbarMessage('Shift has been deleted')
        setSnackbarOpen(true)
        queryClient.invalidateQueries(['getAllShifts'])
        queryClient.invalidateQueries(['getShiftDetails'])
        navigate('/app/management-centre/company-management/time-attendance', {
          state: { snackbarStatus: 3 },
        })
      },
      onError: (error) => {
        setSnackbarStatus('error')
        setSnackbarMessage(error.response?.data || 'An error has occured. Please try again.')
        setSnackbarOpen(true)
        console.log(error)
      },
    }
  )

  const { data: shift, isLoading: shiftIsLoading } = useQuery(
    ['getShiftDetails', params.shiftId],
    async () => {
      if (params.shiftId) {
        const { data } = await getShiftDetails(params.shiftId)
        return data
      }
    }
  )
  const shiftData = shift?.shifts

  const [workingMode, setWorkingMode] = useState(1)

  useEffect(() => {
    if (shiftData?.working_mode_status_id) {
      setWorkingMode(shiftData?.working_mode_status_id)
    }
  }, [shiftData?.working_mode_status_id])

  const [lateSwitch, setLateSwitch] = useState(false)

  useEffect(() => {
    setLateSwitch(shiftData?.late === 1 ? true : false)
  }, [shiftData])

  useEffect(() => {
    // Late type data is directly used from shiftData when needed
  }, [shiftData?.late_last_clockin, shiftData?.late_duration])

  const {
    register,
    handleSubmit,
    control,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: shiftData?.name || null,
      description: shiftData?.description || null,
      late_duration: shiftData?.late_duration || dropdownOptions.clock_in_window[0]?.value,
      late_last_clockin: shiftData?.late_last_clockin || dropdownOptions.time[0]?.value,
    },
  })

  useEffect(() => {
    if (shiftData) {
      reset({
        name: shiftData?.name || null,
        description: shiftData?.description || null,
        late_duration: shiftData?.late_duration || dropdownOptions.clock_in_window[0]?.value,
        late_last_clockin: shiftData?.late_last_clockin || dropdownOptions.time[0]?.value,
      })
    }
  }, [shiftData, reset])

  const [isDraft, setIsDraft] = useState(false)
  const [noExpiryDate, setNoExpiryDate] = useState(false)
  const dateToday = new Date()
  // const isDevelopment = process.env.NODE_ENV === 'development'
  const [selectedStartDate, setSelectedStartDate] = useState(null)
  const [selectedEndDate, setSelectedEndDate] = useState(null)
  const [absentSwitch, setAbsentSwitch] = useState(false)

  // group type default 0: disabled, 1: department, 2: employee
  const [groupType, setGroupType] = useState(0)
  const [selectedDepartments, setSelectedDepartments] = useState([])
  const [addedDepartments, setAddedDepartments] = useState([])
  const [addedEmployees, setAddedEmployees] = useState([])

  const isInactiveAndPastStart = shiftData?.status === 'inactive' && dateToday > selectedStartDate
  const isActiveShift = shiftData?.status === 'active'
  const isScheduledShift = shiftData?.status === 'scheduled'
  const isFutureEffectiveEdit = editable && isActiveShift

  const {
    permission,
    coordinates,
    locationLoading,
    locationData,
    geocodingLoading,
    geocodingError,
    getUserLocation,
  } = useLocationDetection({
    onError: () => {
      setSnackbarStatus('error')
      setSnackbarMessage('Failed to detect location.')
      setSnackbarOpen(true)
    }
  })

  const debouncedLocationSearch = useDebounce(locationSearch, 400)

  const { data: locationSearchResults, isLoading: locationSearchLoading } = useQuery(
    [
      'searchLocations',
      debouncedLocationSearch,
      coordinates?.latitude,
      coordinates?.longitude,
    ],
    async () => {
      const response = await searchLocations({
        query: debouncedLocationSearch.trim(),
        latitude: coordinates?.latitude,
        longitude: coordinates?.longitude,
        limit: 10,
      })

      return response?.data?.results || []
    },
    {
      refetchOnWindowFocus: false,
      enabled: restrictByLocation && debouncedLocationSearch.trim().length >= 2,
    }
  )

  // useEffect(() => {
  //   if (shiftData?.start_date) {
  //     setSelectedStartDate(new Date(shiftData.start_date))
  //   } else {
  //     // Allow today's date only in development environment
  //     setSelectedStartDate(isDevelopment ? new Date() : addDays(new Date(), 1))
  //   }
  //   if (shiftData?.end_date) {
  //     setSelectedEndDate(new Date(shiftData.end_date))
  //   } else {
  //     setSelectedEndDate(isDevelopment ? addDays(new Date(), 1) : addDays(new Date(), 2))
  //   }
  // }, [shiftData?.start_date, shiftData?.end_date, isDevelopment])

  useEffect(() => {
    if (isEdit && shiftData) {
      setSelectedStartDate(shiftData?.start_date ? new Date(shiftData.start_date) : null)
      setSelectedEndDate(shiftData?.end_date ? new Date(shiftData.end_date) : null)
      return
    }

    if (!isEdit) {
      const today = new Date()
      setSelectedStartDate(today)
      setSelectedEndDate(addDays(today, 1))
    }
  }, [isEdit, shiftData])

  useEffect(() => {
    if (!isEdit) {
      setRestrictByLocation(false)
      setSelectedLocation(DEFAULT_LOCATION_FIELDS)
      setSelectedRadius(200)
      setCustomRadiusValue('')
      setCustomRadiusUnit('m')
      setOutsideZoneAction('block')
      setLocationSearch('')
      return
    }

    if (!shiftData) return

    const locationConfig = getLocationConfigFromShift(shiftData)
    const allowedRadiusMeters = Number(locationConfig.allowedRadiusMeters)
    const isPresetRadius = [100, 200, 500, 1000].includes(allowedRadiusMeters)

    setRestrictByLocation(locationConfig.enabled)
    setSelectedLocation({
      name: locationConfig.locationName || '',
      address: locationConfig.locationAddress || '',
      latitude:
        locationConfig.locationLatitude === '' || locationConfig.locationLatitude == null
          ? ''
          : String(locationConfig.locationLatitude),
      longitude:
        locationConfig.locationLongitude === '' || locationConfig.locationLongitude == null
          ? ''
          : String(locationConfig.locationLongitude),
    })
    setOutsideZoneAction(locationConfig.outOfRadiusAction)
    setLocationSearch('')

    if (Number.isInteger(allowedRadiusMeters) && allowedRadiusMeters > 0) {
      if (isPresetRadius) {
        setSelectedRadius(allowedRadiusMeters)
        setCustomRadiusValue('')
        setCustomRadiusUnit('m')
      } else {
        const useKilometers = allowedRadiusMeters >= 1000 && allowedRadiusMeters % 1000 === 0
        setSelectedRadius('custom')
        setCustomRadiusValue(
          useKilometers ? String(allowedRadiusMeters / 1000) : String(allowedRadiusMeters)
        )
        setCustomRadiusUnit(useKilometers ? 'km' : 'm')
      }
    } else {
      setSelectedRadius(200)
      setCustomRadiusValue('')
      setCustomRadiusUnit('m')
    }
  }, [isEdit, shiftData])

  useEffect(() => {
    if (shiftData?.status === 'draft') {
      setIsDraft(true)
    }
  }, [shiftData?.status])

  useEffect(() => {
    if (!applyDetectedLocationOnResolve || !locationData || !coordinates) return

    setSelectedLocation({
      name:
        locationData?.building_name ||
        locationData?.primary_location ||
        locationData?.formatted_address ||
        locationData?.display_name ||
        '',
      address: locationData?.formatted_address || locationData?.display_name || '',
      latitude: String(locationData?.resolved_latitude ?? coordinates?.latitude ?? ''),
      longitude: String(locationData?.resolved_longitude ?? coordinates?.longitude ?? ''),
    })
    setLocationSearch(locationData?.formatted_address || locationData?.display_name || '')
    clearErrors([
      'location_name',
      'location_address',
      'location_latitude',
      'location_longitude',
    ])
    setApplyDetectedLocationOnResolve(false)
  }, [applyDetectedLocationOnResolve, clearErrors, coordinates, locationData])

  const handleUseCurrentLocation = useCallback(() => {
    setApplyDetectedLocationOnResolve(true)
    getUserLocation()
  }, [getUserLocation])

  const handleSelectedLocationChange = useCallback(
    (field) => (event) => {
      const { value } = event.target

      setSelectedLocation((prev) => ({
        ...prev,
        [field]: value,
      }))

      const fieldErrorMap = {
        name: 'location_name',
        address: 'location_address',
        latitude: 'location_latitude',
        longitude: 'location_longitude',
      }

      clearErrors(fieldErrorMap[field])
    },
    [clearErrors]
  )

  const handleLocationSearchSelect = useCallback(
    (_, option) => {
      if (!option) return

      setSelectedLocation({
        name: option?.name || '',
        address: option?.address || option?.display_name || '',
        latitude:
          option?.latitude == null || option?.latitude === '' ? '' : String(option.latitude),
        longitude:
          option?.longitude == null || option?.longitude === '' ? '' : String(option.longitude),
      })
      setLocationSearch(option?.display_name || option?.address || option?.name || '')
      clearErrors([
        'location_name',
        'location_address',
        'location_latitude',
        'location_longitude',
      ])
    },
    [clearErrors]
  )

  useEffect(() => {
    if (shiftData?.shift_assignment?.individuals && employees) {
      const initialAddedEmployees = shiftData.shift_assignment.individuals
        .map((assigneeId) => employees.find((emp) => emp.id === assigneeId))
        .filter(Boolean)

      setAddedEmployees(initialAddedEmployees)
    }
  }, [shiftData, employees])

  useEffect(() => {
    if (shiftData?.shift_assignment?.department && departments) {
      const initialAddedDepartments = shiftData.shift_assignment.department
        .map((assigneeId) => departments.find((dept) => dept.id === assigneeId))
        .filter(Boolean)

      setAddedDepartments(initialAddedDepartments)
      setSelectedDepartments(initialAddedDepartments)
    }
  }, [shiftData, departments])

  const assignedEmployees = useMemo(() => {
    return (
      shiftData?.shift_assignment?.individuals?.map((assigneeId) => {
        const employee = employees?.find((emp) => emp.id === assigneeId)
        return employee ? employee : null
      }) || []
    )
  }, [shiftData?.shift_assignment?.individuals, employees])

  const assignedDepartments = useMemo(() => {
    return (
      shiftData?.shift_assignment?.department?.map((assigneeId) => {
        const department = departments?.find((dept) => dept.id === assigneeId)
        return department ? department : null
      }) || []
    )
  }, [shiftData?.shift_assignment?.department, departments])

  const combinedAssignments = useMemo(() => {
    return [...assignedDepartments, ...assignedEmployees]
  }, [assignedDepartments, assignedEmployees])

  const updateAssignees = () => {
    if (params.shiftId && shiftData?.shift_assignment?.individuals) {
      const updatedEmployees = shiftData?.shift_assignment?.individuals?.map((assigneeId) => {
        const employee = employees?.find((emp) => emp.id === assigneeId)
        if (employee) {
          return {
            ...employee,
          }
        } else {
          return []
        }
      })

      return setAddedEmployees(updatedEmployees)
    }
    if (params.shiftId && shiftData?.shift_assignment?.department) {
      const updatedDepartments = shiftData?.shift_assignment?.department?.map((assigneeId) => {
        const department = departments?.find((dept) => dept.id === assigneeId)
        if (department) {
          return {
            ...department,
          }
        } else {
          return []
        }
      })

      setAddedEmployees(updatedDepartments)
      setSelectedDepartments(updatedDepartments)
      return
    }
    return []
  }

  const [isSameHours, setIsSameHours] = useState(true)
  const [hours, setHours] = useState({
    start_time: '09:00',
    end_time: '17:00',
    total_hours: '08:00',
  })

  const [selectedRadius, setSelectedRadius] = useState(null)
  const [customRadiusValue, setCustomRadiusValue] = useState('')
  const [customRadiusUnit, setCustomRadiusUnit] = useState('m')
  const [outsideZoneAction, setOutsideZoneAction] = useState('block')

  useEffect(() => {
    if (!restrictByLocation) {
      clearErrors([
        'location_name',
        'location_address',
        'location_latitude',
        'location_longitude',
        'allowed_radius_meters',
        'out_of_radius_action',
      ])
    }
  }, [clearErrors, restrictByLocation])

  useEffect(() => {
    if (!restrictByLocation) return
    clearErrors('allowed_radius_meters')
  }, [clearErrors, customRadiusUnit, customRadiusValue, restrictByLocation, selectedRadius])

  useEffect(() => {
    if (!restrictByLocation || !['block', 'warn'].includes(outsideZoneAction)) return
    clearErrors('out_of_radius_action')
  }, [clearErrors, outsideZoneAction, restrictByLocation])

  const [workingHours, setWorkingHours] = useState([
    {
      day: 'Monday',
      start_time: '09:00',
      end_time: '17:00',
      total_hours: '08:00',
      working_day: true,
    },
    {
      day: 'Tuesday',
      start_time: '09:00',
      end_time: '17:00',
      total_hours: '08:00',
      working_day: true,
    },
    {
      day: 'Wednesday',
      start_time: '09:00',
      end_time: '17:00',
      total_hours: '08:00',
      working_day: true,
    },
    {
      day: 'Thursday',
      start_time: '09:00',
      end_time: '17:00',
      total_hours: '08:00',
      working_day: true,
    },
    {
      day: 'Friday',
      start_time: '09:00',
      end_time: '17:00',
      total_hours: '08:00',
      working_day: true,
    },
    {
      day: 'Saturday',
      start_time: '09:00',
      end_time: '17:00',
      total_hours: '08:00',
      working_day: false,
    },
    {
      day: 'Sunday',
      start_time: '09:00',
      end_time: '17:00',
      total_hours: '08:00',
      working_day: false,
    },
  ])

  const updateWorkingHours = () => {
    if (params.shiftId && shiftData?.working_time) {
      const updatedWorkingHours = workingHours.map((item) => {
        const matchingDay = shiftData.working_time.find((element) => element.day === item.day)
        if (matchingDay) {
          if (matchingDay.start_time) {
            return {
              ...item,
              start_time: matchingDay.start_time,
              end_time: matchingDay.end_time,
              total_hours: calculateTimeDifferenceFormatted(
                matchingDay.start_time,
                matchingDay.end_time
              ),
              working_day: true,
            }
          } else {
            return {
              ...item,
              total_hours: matchingDay.total_hours,
              working_day: true,
            }
          }
        } else {
          return { ...item, working_day: false }
        }
      })

      return setWorkingHours(updatedWorkingHours)
    }
    return []
  }

  useEffect(() => {
    if (!params.shiftId || !shiftData?.working_time) return

    const [first, ...rest] = shiftData.working_time

    const isSame = rest.every((item) => {
      if (workingMode === 1) {
        return item.start_time === first.start_time && item.end_time === first.end_time
      } else {
        return item.total_hours === first.total_hours
      }
    })

    setIsSameHours(isSame)
  }, [params.shiftId, shiftData?.working_time, workingMode])

  useEffect(() => {
    if (params.shiftId && shiftData?.working_time && isSameHours) {
      if (shiftData?.working_time[0]?.start_time && shiftData?.working_time[0]?.end_time) {
        return setHours({
          ...hours,
          start_time: shiftData?.working_time[0]?.start_time,
          end_time: shiftData?.working_time[0]?.end_time,
        })
      } else {
        return setHours({ ...hours, total_hours: shiftData?.working_time[0]?.total_hours })
      }
    } else {
      return null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSameHours, params.shiftId, shiftData?.working_time])

  const sections = getSections({
    register,
    control,
    setSelectedStartDate,
    selectedStartDate,
    setSelectedEndDate,
    selectedEndDate,
    noExpiryDate,
    setNoExpiryDate,
    absentSwitch,
    setAbsentSwitch,
    lateSwitch,
    setLateSwitch,
    departments,
    selectedDepartments,
    setSelectedDepartments,
    employees,
    addedEmployees,
    setAddedEmployees,
    groupType,
    setGroupType,
    addedDepartments,
    setAddedDepartments,
    workingHours,
    setWorkingHours,
    workingMode,
    setWorkingMode,
    isSameHours,
    setIsSameHours,
    hours,
    setHours,
    shiftData,
    shiftIsLoading,
    errors,
    editable,
    assignedEmployees,
    assignedDepartments,
    combinedAssignments,
    isInactiveAndPastStart,
    selectedRadius,
    setSelectedRadius,
    customRadiusValue,
    setCustomRadiusValue,
    customRadiusUnit,
    setCustomRadiusUnit,
    outsideZoneAction,
    setOutsideZoneAction,
    permission,
    coordinates,
    locationLoading,
    locationData,
    geocodingLoading,
    geocodingError,
    onUseCurrentLocation: handleUseCurrentLocation,
    restrictByLocation,
    setRestrictByLocation,
    selectedLocation,
    setSelectedLocation: handleSelectedLocationChange,
    locationSearch,
    setLocationSearch,
    hasLocationSearchSupport,
    locationSearchResults,
    locationSearchLoading,
    onLocationSearchSelect: handleLocationSearchSelect,
  })
  const onSubmit = (data) => {
    const { late, late_duration, late_last_clockin, ...remainingData } = data

    const filteredWorkingHours = workingHours
      .filter((item) => item.working_day === true)
      .map((item) => {
        let updatedValue = { ...item }

        if (isSameHours) {
          updatedValue = {
            ...updatedValue,
            start_time: hours.start_time,
            end_time: hours.end_time,
            total_hours: hours.total_hours,
          }
        }

        if (workingMode === 1) {
          delete updatedValue.total_hours
          delete updatedValue.working_day
        } else if (workingMode === 2) {
          delete updatedValue.start_time
          delete updatedValue.end_time
          delete updatedValue.working_day
        }

        return updatedValue
      })

    let allowedRadiusMeters = null

    if (restrictByLocation) {
      const locationName = selectedLocation.name.trim()
      const locationAddress = selectedLocation.address.trim()
      const locationLatitude = Number(selectedLocation.latitude)
      const locationLongitude = Number(selectedLocation.longitude)

      if (!locationName) {
        setError('location_name', { type: 'manual', message: 'Location name is required.' })
      }
      if (!locationAddress) {
        setError('location_address', {
          type: 'manual',
          message: 'Location address is required.',
        })
      }
      if (!Number.isFinite(locationLatitude)) {
        setError('location_latitude', {
          type: 'manual',
          message: 'Latitude is required.',
        })
      }
      if (!Number.isFinite(locationLongitude)) {
        setError('location_longitude', {
          type: 'manual',
          message: 'Longitude is required.',
        })
      }

      allowedRadiusMeters =
        selectedRadius === 'custom'
          ? customRadiusUnit === 'km'
            ? Number(customRadiusValue) * 1000
            : Number(customRadiusValue)
          : Number(selectedRadius)

      if (!Number.isInteger(allowedRadiusMeters) || allowedRadiusMeters <= 0) {
        setError('allowed_radius_meters', {
          type: 'manual',
          message: 'Allowed radius must be a positive integer.',
        })
      }

      if (!['block', 'warn'].includes(outsideZoneAction)) {
        setError('out_of_radius_action', {
          type: 'manual',
          message: 'Select how clock-in should behave outside the radius.',
        })
      }

      if (
        !locationName ||
        !locationAddress ||
        !Number.isFinite(locationLatitude) ||
        !Number.isFinite(locationLongitude) ||
        !Number.isInteger(allowedRadiusMeters) ||
        allowedRadiusMeters <= 0 ||
        !['block', 'warn'].includes(outsideZoneAction)
      ) {
        setSnackbarStatus('error')
        setSnackbarMessage('Complete the default location details before saving this shift.')
        setSnackbarOpen(true)
        return
      }
    }

    const body = {
      ...remainingData,
      start_date: isInactiveAndPastStart
        ? shiftData?.start_date
        : formatToYYYYMMDD(selectedStartDate),
      ...(!noExpiryDate ? { end_date: formatToYYYYMMDD(selectedEndDate) } : { end_date: '' }),
      working_mode_status_id: workingMode,
      late: lateSwitch,
      ...(lateSwitch && workingMode === 1 && { late_duration }),
      ...(lateSwitch && workingMode === 2 && { late_last_clockin }),
      save_as_draft: isDraft,
      shift_assignment: {
        individuals: addedEmployees.map((emp) => emp.id),
        department: addedDepartments.map((dept) => dept.id),
      },
      ...(workingMode && { working_time: filteredWorkingHours }),
      use_default_location: restrictByLocation,
      ...(restrictByLocation
        ? {
          location_name: selectedLocation.name.trim(),
          location_address: selectedLocation.address.trim(),
          location_latitude: Number(selectedLocation.latitude),
          location_longitude: Number(selectedLocation.longitude),
          allowed_radius_meters: allowedRadiusMeters,
          out_of_radius_action: outsideZoneAction,
        }
        : {
          location_name: '',
          location_address: '',
          location_latitude: null,
          location_longitude: null,
          allowed_radius_meters: null,
          out_of_radius_action: null,
        }),
      ...(isFutureEffectiveEdit ? { effective_from: 'tomorrow' } : {}),
    }
    if (params.shiftId) {
      mutateEditShift(body)
    } else {
      mutateCreateShift(body)
    }
  }

  useEffect(() => {
    if (params.shiftId) {
      if (shiftData?.status === 'draft') {
        setEditable(true)
        updateWorkingHours()
      } else {
        setEditable(false)
        updateWorkingHours()
        updateAssignees()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.shiftId, shiftData?.status])

  useEffect(() => {
    setNoExpiryDate(shiftData?.end_date == null)
  }, [shiftData])

  const [activeSection, setActiveSection] = useState(null)

  const scrollToSection = (e) => {
    const section = document.getElementById(e)
    section.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    if (shiftIsLoading) return
    const observerOptions = {
      root: null,
      rootMargin: '-90px 0px -40% 0px',
      threshold: 0.3,
    }

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id)
        }
      })
    }

    const observer = new IntersectionObserver(observerCallback, observerOptions)

    sectionRefs.current.forEach((section) => {
      if (section) observer.observe(section)
    })

    return () => {
      observer.disconnect()
    }
  }, [shiftIsLoading])

  if (shiftIsLoading) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          color: 'alternate.main',
        }}
      >
        <CircularProgress color={'inherit'} size={40} />
      </Box>
    )
  } else {
    return (
      <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
        <Box
          sx={{
            backgroundColor: 'gray-background.main',
            height: '100%',
            width: '100%',
          }}
        >
          <SnackbarRecruitment
            autoHideDuration={3000}
            status={snackbarStatus}
            TransitionComponent="slide"
            message={snackbarMessage}
            open={snackbarOpen}
            handleClose={() => setSnackbarOpen(false)}
          />
          <Box
            sx={{
              backgroundColor: 'white.main',
              position: 'sticky',
              zIndex: 1,
              top: 0,
              padding: '16px 24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              borderBottom: '1px solid #E5E5E5',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {`${shiftData?.name
                  ? `${shiftData?.name} (${shiftData?.working_time?.length === 7
                    ? 'All'
                    : shiftData?.working_time?.length
                  } days)`
                  : 'Create a new shift'
                  }`}
              </Typography>
              {shiftData?.status !== undefined && (
                <>
                  <Typography sx={{ color: 'light-gray-text.main' }}>•</Typography>
                  <Typography sx={{ color: 'light-gray-text.main' }}>{`${shiftData?.staff_count} ${shiftData?.staff_count > 1 ? 'employees' : 'employee'
                    }`}</Typography>
                </>
              )}
              {shiftData?.status && (
                <Box
                  sx={{
                    padding: '12px',
                    borderRadius: '4.5px',
                    backgroundColor:
                      shiftData?.status === 'active'
                        ? 'light-positive.main'
                        : shiftData?.status === 'scheduled'
                          ? 'light-information.main'
                          : shiftData?.status === 'draft'
                            ? 'light-warning.main'
                            : 'gray-background.main',
                    color:
                      shiftData?.status === 'active'
                        ? 'positive.main'
                        : shiftData?.status === 'scheduled'
                          ? 'information.main'
                          : shiftData?.status === 'draft'
                            ? 'warning.main'
                            : 'light-gray-text.main',
                    height: '30px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textTransform: 'capitalize',
                  }}
                >
                  <Typography>{shiftData?.status}</Typography>
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <CloseIcon
                sx={{ cursor: 'pointer' }}
                onClick={() =>
                  navigate('/app/management-centre/company-management/time-attendance')
                }
              />
            </Box>
          </Box>
          <Box
            sx={{
              overflowY: 'scroll',
              height: 'calc(100vh - 70px)',
              // padding: '30px 30px 30px 60px',
              px: 3,
              pt: 3,
              pb: '100px',
            }}
            className="scrollbar"
          >
            <Box sx={{ display: 'flex' }}>
              {/* content */}
              <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '32px' }}>
                {isFutureEffectiveEdit && (
                  <Box
                    sx={{
                      bgcolor: 'warning.light',
                      border: 1,
                      borderColor: 'warning.main',
                      borderRadius: '4.5px',
                      px: 3,
                      py: 2,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'dark.main' }}>
                      Changes will apply from tomorrow
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'light-gray-text.main', mt: 0.5 }}>
                      This shift is active today, so today&apos;s attendance records will stay intact.
                    </Typography>
                  </Box>
                )}
                {sections.map((item, index) => (
                  <Box
                    id={item.title}
                    key={index}
                    ref={(el) => (sectionRefs.current[index] = el)}
                    sx={{
                      backgroundColor: 'white.main',
                      borderRadius: '4.5px',
                      border: 1,
                      borderColor: 'gray-line.main',
                    }}
                  >
                    <Box
                      sx={{
                        padding: '16px 24px',
                        borderBottom: 1,
                        borderBottomColor: 'gray-line.main',
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {item.title}
                      </Typography>
                    </Box>
                    <Box sx={{ padding: '24px' }}>{item.content}</Box>
                  </Box>
                ))}
              </Box>
              {/* form section indicator */}
              <Box
                id="sectionIndicator"
                sx={{
                  paddingX: '30px',
                  width: '320px',
                  display: 'flex',
                  flexDirection: 'column',
                  height: 'max-content',
                  position: 'sticky',
                  zIndex: 1,
                  top: 0,
                }}
              >
                {sections.map((item) => (
                  <Box
                    key={item.title}
                    sx={{
                      borderLeft: 1,
                      borderColor:
                        activeSection === item.title ? 'alternate.main' : 'gray-line.main',
                      borderLeftWidth: '3px',
                      cursor: 'pointer',
                      color:
                        activeSection === item.title ? 'alternate.main' : 'light-gray-text.main',
                      ':hover': {
                        color: 'alternate.main',
                        backgroundColor: 'alternate.light',
                        borderColor: 'alternate.main',
                      },
                    }}
                    onClick={() => scrollToSection(item.title)}
                  >
                    <Typography sx={{ padding: '12px 16px' }}>{item.title}</Typography>
                  </Box>
                ))}
                {/* <Box
                  sx={{ marginTop: '10vh', display: 'flex', flexDirection: 'column', gap: '10px' }}
                >
                  {(shiftData?.status === 'active' || shiftData?.status === 'scheduled') && (
                    <AdxButton
                      isLoading={activateOrDeactivateShiftIsLoading}
                      disabled={activateOrDeactivateShiftIsLoading}
                      variant={'outlined-destructive'}
                      onClick={handleDisableShift}
                    >
                      {shiftData?.status === 'active' ? 'Disable shift' : 'Deactivate shift'}
                    </AdxButton>
                  )}
                  {editable &&
                    (shiftData?.status === 'inactive' || shiftData?.status === 'draft') && (
                      <AdxButton
                        type="button"
                        disabled={mutateEditShiftIsLoading}
                        isLoading={mutateEditShiftIsLoading}
                        variant={'contained'}
                        onClick={() => handleSubmit(onSubmit)()}
                      >
                        Save changes
                      </AdxButton>
                    )}
                  {!editable &&
                    params.shiftId &&
                    (shiftData?.status === 'inactive' || shiftData?.status === 'draft') && (
                      <AdxButton
                        type="button"
                        variant={'outlined'}
                        onClick={() => {
                          setEditable(true)
                        }}
                      >
                        Edit
                      </AdxButton>
                    )}
                  {(shiftData?.status === 'inactive' || shiftData?.status === 'draft') && (
                    <AdxButton
                      isLoading={mutateDeleteShiftIsLoading}
                      disabled={mutateDeleteShiftIsLoading}
                      variant={'outlined-destructive'}
                      onClick={() => mutateDeleteShift(params.shiftId)}
                    >
                      Delete shift
                    </AdxButton>
                  )}
                </Box> */}

                <Box sx={{ marginTop: '10vh', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {(shiftData?.status === 'inactive' || shiftData?.status === 'draft') && (
                    <AdxButton
                      isLoading={mutateDeleteShiftIsLoading}
                      disabled={mutateDeleteShiftIsLoading}
                      variant={'outlined-destructive'}
                      onClick={() => mutateDeleteShift(params.shiftId)}
                    >
                      Delete shift
                    </AdxButton>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              width: '100%',
              display: 'flex',
              justifyContent: { xs: 'center', md: 'end' },
              alignItems: 'center',
              bgcolor: 'primary.light',
              px: 3,
              py: 2,
              gap: 1,
              borderTop: '1px solid',
              borderColor: 'grayscale.line-light',
            }}
          >
            {/* CREATE MODE */}
            {shiftData?.status === undefined && (
              <>
                <AdxButton
                  variant={'outlined'}
                  type="submit"
                  onClick={() => setIsDraft(true)}
                  disabled={mutateCreateShiftIsLoading || mutateEditShiftIsLoading}
                  isLoading={mutateCreateShiftIsLoading || mutateEditShiftIsLoading}
                >
                  Save as draft
                </AdxButton>
                <AdxButton
                  variant={'contained'}
                  sx={{ width: '250px' }}
                  type="submit"
                  onClick={() => setIsDraft(false)}
                  disabled={mutateCreateShiftIsLoading || mutateEditShiftIsLoading}
                  isLoading={mutateCreateShiftIsLoading || mutateEditShiftIsLoading}
                >
                  Schedule
                </AdxButton>
              </>
            )}

            {/* ACTIVE / SCHEDULED */}
            {!editable && (isActiveShift || isScheduledShift) && (
              <>
                {isActiveShift && (
                  <AdxButton
                    variant={'outlined'}
                    sx={{ width: '250px' }}
                    onClick={() => setEditable(true)}
                  >
                    Edit
                  </AdxButton>
                )}
                <AdxButton
                  isLoading={activateOrDeactivateShiftIsLoading}
                  disabled={activateOrDeactivateShiftIsLoading}
                  variant={'outlined-destructive'}
                  sx={{ width: '250px' }}
                  onClick={handleDisableShift}
                >
                  {isActiveShift ? 'Disable shift' : 'Deactivate shift'}
                </AdxButton>
              </>
            )}

            {editable && isActiveShift && (
              <AdxButton
                type="button"
                disabled={mutateEditShiftIsLoading}
                isLoading={mutateEditShiftIsLoading}
                variant={'contained'}
                sx={{ width: '250px' }}
                onClick={() => handleSubmit(onSubmit)()}
              >
                Apply starting tomorrow
              </AdxButton>
            )}

            {/* INACTIVE/DRAFT, READ-ONLY: Edit + Activate/Schedule */}
            {!editable && (shiftData?.status === 'inactive' || shiftData?.status === 'draft') && (
              <>
                <AdxButton
                  variant={'outlined'}
                  sx={{ width: '250px' }}
                  onClick={() => setEditable(true)}
                >
                  Edit
                </AdxButton>
                {shiftData?.status === 'inactive' && (
                  <AdxButton
                    disabled={activateOrDeactivateShiftIsLoading}
                    isLoading={activateOrDeactivateShiftIsLoading}
                    variant="contained"
                    sx={{ width: '250px' }}
                    onClick={() => activateOrDeactivateShift({ status: 'active' })}
                  >
                    Activate
                  </AdxButton>
                )}
                {shiftData?.status === 'draft' && (
                  <AdxButton
                    isLoading={mutateCreateShiftIsLoading || mutateEditShiftIsLoading}
                    variant={'contained'}
                    sx={{ width: '250px' }}
                    type="submit"
                    onClick={() => setIsDraft(false)}
                    disabled={mutateCreateShiftIsLoading || mutateEditShiftIsLoading}
                  >
                    Schedule
                  </AdxButton>
                )}
              </>
            )}

            {/* INACTIVE/DRAFT, EDIT MODE: Save changes only */}
            {editable && (shiftData?.status === 'inactive' || shiftData?.status === 'draft') && (
              <AdxButton
                type="button"
                disabled={mutateEditShiftIsLoading}
                isLoading={mutateEditShiftIsLoading}
                variant={'contained'}
                sx={{ width: '250px' }}
                onClick={() => handleSubmit(onSubmit)()}
              >
                Save changes
              </AdxButton>
            )}
          </Box>
          <AdxInfoModal
            open={disableImpactModalOpen}
            onClose={() => setDisableImpactModalOpen(false)}
            handleClose={() => setDisableImpactModalOpen(false)}
            variant="warning"
            muiIcon="warning"
            iconColor="warning.main"
            title={`${disableImpact?.data?.active_clockins_count || 0} employee${(disableImpact?.data?.active_clockins_count || 0) > 1 ? 's are' : ' is'} currently clocked in`}
            subtitle="Disabling this shift now would affect today's running attendance. Disable it from tomorrow to keep today's clock-in records intact."
            footerButtons
            firstBtnLabel="Disable from tomorrow"
            firstBtnHandle={handleDisableFromTomorrow}
            firstBtnLoading={activateOrDeactivateShiftIsLoading}
            firstBtnDisabled={activateOrDeactivateShiftIsLoading}
            secondBtnLabel="Cancel"
            secondBtnHandle={() => setDisableImpactModalOpen(false)}
            secondBtnDisabled={activateOrDeactivateShiftIsLoading}
          >
            {disableImpact?.data?.active_clockins?.length ? (
              <Box width={1} display="flex" flexDirection="column" gap={0.5}>
                {disableImpact.data.active_clockins.slice(0, 5).map((item) => (
                  <Typography key={item.attendance_id} variant="body2" color="light-gray-text.main">
                    {item.name || `Staff #${item.staff_id}`} clocked in at {item.clockin_at || '-'}
                  </Typography>
                ))}
                {disableImpact.data.active_clockins.length > 5 ? (
                  <Typography variant="body2" color="light-gray-text.main">
                    +{disableImpact.data.active_clockins.length - 5} more
                  </Typography>
                ) : null}
              </Box>
            ) : null}
          </AdxInfoModal>
        </Box>
      </form>
    )
  }
}

export default ShiftForm
